import { memo, useState } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useAuthContext } from "@/providers/authProvider/authContext";
import RemainingCount from "./RemainingCount";
import {
  StyledContainer,
  StyledMobileButton,
  StyledLogoutButton,
  StyledLoginButton,
} from "./UserInfo.styles";
import { FlexBox } from "@/components/baseComponents/FlexBox";
import Person from "@/components/icons/Person";
import { useNavigate } from "@tanstack/react-router";
import { IS_ENABLE_GENERATION_COUNT_LIMIT } from "@/utils/constants";

const UserInfo = () => {
  const { user, logout } = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate({ to: "/" });
  };

  if (!user) {
    return (
      <StyledLoginButton
        onClick={() => navigate({ to: "/login" })}
        variant="contained"
      >
        登入
      </StyledLoginButton>
    );
  }

  // 小螢幕：顯示為按鈕，點擊後顯示 Menu
  if (isMobile) {
    return (
      <>
        <StyledMobileButton onClick={handleClick}>
          <Person width={32} height={32} />
          <ArrowDropDownIcon sx={{ fontSize: "32px" }} />
        </StyledMobileButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              minWidth: "200px",
            },
          }}
        >
          <MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
            <FlexBox gap={1}>
              <Person
                width={32}
                height={32}
                color1={theme.palette.primary.main}
                color2={theme.palette.primary.main}
                showLinerGradient={false}
              />
              <Typography variant="subtitle1">{user.fullName}</Typography>
            </FlexBox>
          </MenuItem>
          {IS_ENABLE_GENERATION_COUNT_LIMIT && (
            <MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
              <RemainingCount variant="secondary" />
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>登出</MenuItem>
        </Menu>
      </>
    );
  }

  return (
    <StyledContainer>
      {IS_ENABLE_GENERATION_COUNT_LIMIT && (
        <>
          <RemainingCount variant="primary" />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: theme.palette.grey[400] }}
          />
        </>
      )}
      <FlexBox gap={1}>
        <Person width={32} height={32} />
        <Typography variant="subtitle1">{user.fullName}</Typography>
      </FlexBox>
      <StyledLogoutButton onClick={handleLogout} variant="text">
        登出
      </StyledLogoutButton>
    </StyledContainer>
  );
};

export default memo(UserInfo);
